/**
 * we need to override behavior for some function from
 * RangeSelection and LexicalUpdate and Normalization
 */

import {
  $getSelection,
  $isParagraphNode,
  $isRangeSelection,
  $isTextNode,
  ElementNode,
  PointType,
  RangeSelection,
  TextNode,
} from "lexical";
import {
  $createReviewTextNode,
  $isReviewTextNode,
  ReviewTextNode,
} from "./ReviewTextNode";


// mimic deleteWord()
// TODO: resolve error when doing backspace at the start of the paragraph
export function $markForDelete(
  selection: RangeSelection,
  isBackward: boolean,
): void {
  // If selection is collapsed, extend it to select something to delete
  if (selection.isCollapsed()) {
    selection.modify("extend", isBackward, "character");
  }

  // If we still don't have a selection, nothing to delete
  if (selection.isCollapsed()) {
    return;
  }

  // Get the selected text and nodes
  const selectedText = selection.getTextContent();
  if (!selectedText) {
    return;
  }

  // Handle the deletion based on the selected nodes
  const nodes = selection.getNodes();
  
  // Simple approach: replace selected content with track changes
  if (nodes.length === 1) {
    const node = nodes[0];
    const { anchor, focus } = selection;
    const startOffset = Math.min(anchor.offset, focus.offset);
    const endOffset = Math.max(anchor.offset, focus.offset);
    
    if ($isReviewTextNode(node)) {
      handleReviewNodeDeletion(node, startOffset, endOffset);
    } else if ($isTextNode(node)) {
      handleTextNodeDeletion(node, startOffset, endOffset);
    }
  } else {
    // Multi-node deletion
    handleMultiNodeDeletion(nodes, selection);
  }
}

// Handle deletion in a single ReviewTextNode
function handleReviewNodeDeletion(node: ReviewTextNode, startOffset: number, endOffset: number): void {
  if (node.hasReviewType("insertion")) {
    // For insertion nodes, actually delete the text
    node.deleteInsertionText(startOffset, endOffset - startOffset);
  } else if (node.hasReviewType("deletion")) {
    // For deletion nodes, revert to original
    const originalNode = node.setReviewType("original");
    originalNode.select(startOffset, startOffset);
  } else {
    // For original nodes, mark as deletion
    const nodes = node.deleteOriginalText(startOffset, endOffset - startOffset);
    if (nodes[0] && nodes[0].getTextContentSize() > 0) {
      nodes[0].selectEnd();
    } else if (nodes[1]) {
      nodes[1].selectStart();
    }
  }
}

// Handle deletion in a single regular TextNode
function handleTextNodeDeletion(node: TextNode, startOffset: number, endOffset: number): void {
  const text = node.getTextContent();
  const beforeText = text.slice(0, startOffset);
  const deletedText = text.slice(startOffset, endOffset);
  const afterText = text.slice(endOffset);
  
  const parent = node.getParent();
  if (!parent) return;
  
  // Replace the text node with review nodes
  node.remove();
  
  // Add before text as original
  if (beforeText) {
    const beforeNode = $createReviewTextNode(beforeText, "original");
    parent.append(beforeNode);
  }
  
  // Add deleted text as deletion
  const deletedNode = $createReviewTextNode(deletedText, "deletion");
  parent.append(deletedNode);
  
  // Add after text as original
  if (afterText) {
    const afterNode = $createReviewTextNode(afterText, "original");
    parent.append(afterNode);
    afterNode.selectStart();
  } else {
    deletedNode.selectEnd();
  }
}

// Handle deletion across multiple nodes
function handleMultiNodeDeletion(nodes: any[], selection: RangeSelection): void {
  nodes.forEach((node) => {
    if ($isReviewTextNode(node)) {
      if (node.hasReviewType("insertion")) {
        node.remove();
      } else if (node.hasReviewType("original")) {
        node.setReviewType("deletion");
      }
    } else if ($isTextNode(node)) {
      const deletedNode = $createReviewTextNode(node.getTextContent(), "deletion");
      node.replace(deletedNode);
    }
  });
  
  // Position cursor at start of selection
  const { anchor } = selection;
  const anchorNode = anchor.getNode();
  if ($isReviewTextNode(anchorNode) || $isTextNode(anchorNode)) {
    anchorNode.select(anchor.offset, anchor.offset);
  }
}


function insertReviewTextNode(
  targetNode: ReviewTextNode,
  anchor: PointType,
  text: string,
): void {
  let newReviewTextNode = $createReviewTextNode(text, "insertion");
  // no need to check for inline parent, because we do not have
  // such thing, only parent of paragraph (not inline) is available.
  // this conditional enable insertion from the start of text node
  // of another type for example: inserting 'test' in <del>The</del>
  // will make it <ins>test</ins><del>The</del> not <del>testThe</de>
  if (anchor.offset === 0) {
    targetNode.insertBefore(newReviewTextNode);
    newReviewTextNode.selectEnd();

    // adding this for the similar reason to above conditional
  } else if (anchor.offset === targetNode.getTextContentSize()) {
    targetNode.insertAfter(newReviewTextNode);
    newReviewTextNode.selectEnd();

    // insertion in the middle of node, <orig>this is inside<orig>
    // of text 'not' become <orig>this is</orig><ins>not</ins>
    // <orig> inside</orig>
    // there is no <orig> it just to show demo
  } else {
    newReviewTextNode = targetNode.insertBetweenAddOrOrig(anchor.offset, text);
    if (targetNode.hasReviewType("original")) {
      newReviewTextNode.selectEnd();
    } else {
      const newOffset = anchor.offset + text.length;
      newReviewTextNode.select(newOffset, newOffset);
    }
  }
}

// mimic $insertDataTransferForRichText but only for text/plain
export function $markPasteInsert(text: string): void {
  if (text != null) {
    const parts = text.split(/\r?\n/);
    if (parts[parts.length - 1] === "") {
      parts.pop();
    }
    for (let i = 0; i < parts.length; i++) {
      const currentSelection = $getSelection();
      if ($isRangeSelection(currentSelection)) {
        const part = parts[i];
        if (part === "\n" || part === "\r\n") {
          currentSelection.insertParagraph();
        } else {
          const newReviewTextNode = $createReviewTextNode(part, "insertion");
          currentSelection.insertNodes([newReviewTextNode]);
          newReviewTextNode.selectEnd();
        }
      }
    }
  }
}

// mimic inserText(), only handle typing (no multiline insert)
export function $markTypingInsert(
  selection: RangeSelection,
  text: string,
): void {
  const anchor = selection.anchor;
  const anchorNode = anchor.getNode();
  
  // Handle typing in deletion nodes - don't allow unless at the end
  if ($isReviewTextNode(anchorNode) && 
      anchorNode.hasReviewType("deletion") &&
      anchor.offset !== anchorNode.getTextContentSize()) {
    return; // Don't allow typing in the middle of deleted text
  }
  
  // If we're in a paragraph or other element node, create a new review text node
  if ($isParagraphNode(anchorNode)) {
    const newReviewTextNode = $createReviewTextNode(text, "insertion");
    anchorNode.append(newReviewTextNode);
    newReviewTextNode.selectEnd();
    return;
  }
  
  // If we're in a review text node, handle insertion based on its type
  if ($isReviewTextNode(anchorNode)) {
    if (anchorNode.hasReviewType("insertion")) {
      // If typing in an insertion node, just add to it
      insertReviewTextNode(anchorNode, anchor, text);
    } else {
      // If typing in original text, split and create insertion
      insertReviewTextNode(anchorNode, anchor, text);
    }
    return;
  }
  
  // Fallback: create a new insertion node
  const newReviewTextNode = $createReviewTextNode(text, "insertion");
  if (anchorNode.insertAfter) {
    anchorNode.insertAfter(newReviewTextNode);
  } else {
    const parent = anchorNode.getParent();
    if (parent) {
      parent.append(newReviewTextNode);
    }
  }
  newReviewTextNode.selectEnd();
}
